import _defineProperty from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _slicedToArray from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import _objectWithoutProperties from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["state"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useState } from 'react';
import { useId, useSafeLayoutEffect, useForkRef } from 'ariakit-utils/hooks';
import { createHook, createComponent, createElement } from 'ariakit-utils/system';
import { flushSync } from 'react-dom';

function afterTimeout(timeoutMs, cb) {
  var timeoutId = setTimeout(cb, timeoutMs);
  return function () {
    return clearTimeout(timeoutId);
  };
}

function afterPaint(cb) {
  var raf = requestAnimationFrame(function () {
    raf = requestAnimationFrame(cb);
  });
  return function () {
    return cancelAnimationFrame(raf);
  };
}

function parseCSSTime() {
  for (var _len = arguments.length, times = new Array(_len), _key = 0; _key < _len; _key++) {
    times[_key] = arguments[_key];
  }

  return times.join(", ").split(", ").reduce(function (longestTime, currentTimeString) {
    var currentTime = parseFloat(currentTimeString || "0s") * 1000; // When multiple times are specified, we want to use the longest one so we
    // wait until the longest transition has finished.

    if (currentTime > longestTime) return currentTime;
    return longestTime;
  }, 0);
}
/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component to render an element that can be shown or hidden.
 * @see https://ariakit.org/components/disclosure
 * @example
 * ```jsx
 * const state = useDisclosureState();
 * const props = useDisclosureContent({ state });
 * <Disclosure state={state}>Disclosure</Disclosure>
 * <Role {...props}>Content</Role>
 * ```
 */


var useDisclosureContent = createHook(function (_ref) {
  var state = _ref.state,
      props = _objectWithoutProperties(_ref, _excluded);

  var id = useId(props.id);

  var _useState = useState(null),
      _useState2 = _slicedToArray(_useState, 2),
      transition = _useState2[0],
      setTransition = _useState2[1];

  useSafeLayoutEffect(function () {
    var _state$contentElement;

    if (!state.animated) return; // When the disclosure content element is rendered in a portal, we need to
    // wait for the portal to be mounted and connected to the DOM before we
    // can start the animation.

    if (!((_state$contentElement = state.contentElement) != null && _state$contentElement.isConnected)) {
      setTransition(null);
      return;
    } // Double requestAnimationFrame is necessary here to avoid potential bugs
    // when the data attribute is added before the element is fully rendered
    // in the DOM, which wouldn't trigger the animation.


    return afterPaint(function () {
      setTransition(state.open ? "enter" : "leave");
    });
  }, [state.animated, state.contentElement, state.open]);
  useSafeLayoutEffect(function () {
    if (!state.animated) return;
    if (!state.contentElement) return;
    if (!transition) return;
    if (transition === "enter" && !state.open) return;
    if (transition === "leave" && state.open) return; // When the animated state is a number, the user has manually set the
    // animation timeout, so we just respect it.

    if (typeof state.animated === "number") {
      var _timeoutMs = state.animated;
      return afterTimeout(_timeoutMs, function () {
        return flushSync(state.stopAnimation);
      });
    } // Otherwise, we need to parse the CSS transition/animation duration and
    // delay to know when the animation ends. This is safer than relying on
    // the transitionend/animationend events because it's not guaranteed that
    // these events will fire. For example, if the element is removed from the
    // DOM before the animation ends or if the animation wasn't triggered in
    // the first place, the events won't fire.


    var _getComputedStyle = getComputedStyle(state.contentElement),
        transitionDuration = _getComputedStyle.transitionDuration,
        animationDuration = _getComputedStyle.animationDuration,
        transitionDelay = _getComputedStyle.transitionDelay,
        animationDelay = _getComputedStyle.animationDelay;

    var delay = parseCSSTime(transitionDelay, animationDelay);
    var duration = parseCSSTime(transitionDuration, animationDuration);
    var timeoutMs = delay + duration; // If the animation/transition delay and duration are 0, this means the
    // element is not animated with CSS (they may be using framer-motion,
    // react-spring, or something else). In this case, the user is responsible
    // for calling `stopAnimation` when the animation ends.

    if (!timeoutMs) return; // TODO: We should probably warn if `stopAnimation` hasn't been called
    // after X seconds.

    return afterTimeout(timeoutMs, function () {
      return flushSync(state.stopAnimation);
    });
  }, [state.animated, state.contentElement, transition, state.open, state.stopAnimation]);
  var style = state.mounted || props.hidden === false ? props.style : _objectSpread(_objectSpread({}, props.style), {}, {
    display: "none"
  });
  props = _objectSpread(_objectSpread({
    id: id,
    "data-enter": transition === "enter" ? "" : undefined,
    "data-leave": transition === "leave" ? "" : undefined,
    hidden: !state.mounted
  }, props), {}, {
    ref: useForkRef(id ? state.setContentElement : null, props.ref),
    style: style
  });
  return props;
});
/**
 * A component that renders an element that can be shown or hidden.
 * @see https://ariakit.org/components/disclosure
 * @example
 * ```jsx
 * const disclosure = useDisclosureState();
 * <Disclosure state={disclosure}>Disclosure</Disclosure>
 * <DisclosureContent state={disclosure}>Content</DisclosureContent>
 * ```
 */

var DisclosureContent = createComponent(function (props) {
  var htmlProps = useDisclosureContent(props);
  return createElement("div", htmlProps);
});
export { DisclosureContent, useDisclosureContent };